import React, { useState, useEffect } from 'react';
import DashboardGraph from './DashboardGraph';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  IconButton, 
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Box,
  Paper,
  Tooltip
} from '@material-ui/core';
import { 
  FaChartLine, 
  FaExchangeAlt, 
  FaNewspaper, 
  FaUsers, 
  FaCoins, 
  FaMoneyBillWave,
  FaCubes,
  FaPlus,
  FaArrowRight,
  FaInfoCircle,
  FaHeart,
  FaRegCopy,
  FaNetworkWired,
  FaChartBar
} from 'react-icons/fa';
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';
import { futLogoIcon200, E3D_TOKEN_ABI, E3D_NFT_MANAGER_ABI } from './config';
import axios from 'axios';
import { SERVER_NAME } from './config';

const useStyles = makeStyles((theme) => ({
  // Override MUI Typography for this component
  '@global': {
    '.MuiTypography-root': {
      color: '#ffffff',
    },
    '.MuiTypography-colorTextSecondary': {
      color: '#cccccc',
    },
    '.MuiListItemText-primary': {
      color: '#ffffff',
    },
    '.MuiListItemText-secondary': {
      color: '#cccccc',
    },
  },
  dashboardContainer: {
    padding: 0,
    backgroundColor: '#121212',
    minHeight: '100vh',
    color: '#ffffff',
    margin: 0,
    border: 'none',
    boxSizing: 'border-box'
  },
  navBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px',
    height: 52,
    backgroundColor: '#1a1a1a',
    borderBottom: '1px solid #333',
    zIndex: 1000,
    fontFamily: theme.typography.fontFamily,
  },
  navLogo: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: '#ffffff',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  logoImage: {
    height: 36,
    marginRight: theme.spacing(1),
  },
  logoText: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: '#4d79ff',
  },
  navLinks: {
    display: 'flex',
    marginLeft: theme.spacing(4),
  },
  navLink: {
    color: '#ffffff',
    margin: theme.spacing(0, 2),
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'color 0.2s',
    '&:hover': {
      color: '#4d79ff',
      textDecoration: 'none',
    },
  },
  active: {
    color: '#4d79ff',
  },
  rightNav: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  walletButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#3a66ff',
    },
  },
  mainContent: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center'
  },
  welcomeText: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    background: 'linear-gradient(45deg, #4d79ff, #8c64ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    color: '#cccccc',
    marginBottom: theme.spacing(3)
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: theme.spacing(4),
    flexWrap: 'wrap',
    '& > div': {
      margin: theme.spacing(1)
    }
  },
  statCard: {
    backgroundColor: '#1a1a1a',
    padding: theme.spacing(2),
    borderRadius: 15,
    minWidth: 150,
    textAlign: 'center',
    border: '1px solid #4d79ff',
  },
  statValue: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: '#4d79ff'
  },
  statLabel: {
    color: '#aaaaaa',
    marginTop: theme.spacing(1)
  },
  widgetCard: {
    backgroundColor: '#1f2026',
    borderRadius: 15,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid #4d79ff',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 20px rgba(77, 121, 255, 0.2)',
    },
  },
  widgetTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px solid rgba(77, 121, 255, 0.4)',
    backgroundColor: '#272935',
    color: '#ffffff',
    '& h6': {
      color: '#ffffff',
    },
  },
  widgetContent: {
    padding: theme.spacing(2),
    color: '#f0f0f0',
    height: 'auto',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#4d79ff',
      borderRadius: '4px',
    },
  },
  textSecondary: {
    color: '#cccccc',
  },
  viewAllButton: {
    textTransform: 'none',
    color: '#4d79ff',
    padding: theme.spacing(0.5, 1),
    fontSize: '0.8rem',
    '&:hover': {
      backgroundColor: 'rgba(77, 121, 255, 0.1)',
    },
  },
  graphPreview: {
    width: '100%',
    height: 200,
    backgroundColor: '#000000',
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    position: 'relative',
  },
  marketplaceItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
  },
  nftImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: theme.spacing(2),
    objectFit: 'cover',
    backgroundColor: '#000',
  },
  nftPrice: {
    fontWeight: 'bold',
    color: '#4d79ff',
  },
  tokenItem: {
    padding: theme.spacing(1),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tokenLogo: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: '#333',
  },
  tokenInfo: {
    flex: 1,
    color: '#ffffff',
  },
  tokenName: {
    fontWeight: 500,
    color: '#ffffff',
  },
  tokenPrice: {
    color: '#4d79ff',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  sparkline: {
    height: 30,
    width: 80,
  },
  positiveChange: {
    color: '#00e676',
  },
  negativeChange: {
    color: '#ff5252',
  },
  actionButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    borderRadius: 20,
    padding: theme.spacing(0.5, 2),
    fontSize: '0.8rem',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
  },
  statCard: {
    padding: theme.spacing(1),
    backgroundColor: '#272935',
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '22%',
    height: 80,
    border: '1px solid #4d79ff',
  },
  statValue: {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    color: '#4d79ff',
  },
  statLabel: {
    color: '#aaaaaa',
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  stakingStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
  },
  stakingStatItem: {
    textAlign: 'center',
  },
  stakingStatValue: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#4d79ff',
  },
  stakingStatLabel: {
    fontSize: '0.8rem',
    color: '#aaaaaa',
  },
  newsfeedItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
  },
  newsfeedTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  newsfeedDate: {
    fontSize: '0.8rem',
    color: '#aaaaaa',
  },
  newsfeedExcerpt: {
    fontSize: '0.9rem',
    color: '#cccccc',
    marginTop: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
  },
  activityAvatar: {
    width: 36,
    height: 36,
    marginRight: theme.spacing(1.5),
    backgroundColor: '#333',
  },
  activityContent: {
    flex: 1,
  },
  activityTime: {
    fontSize: '0.75rem',
    color: '#aaaaaa',
  },
  callToAction: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  ctaButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    padding: theme.spacing(1.5, 4),
    borderRadius: 30,
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 150,
  },
  iconWithLabel: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(1),
      color: '#4d79ff',
    },
  }
}));

const Dashboard = ({ walletAddress, isAuthenticated, userID }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [newTokens, setNewTokens] = useState([]);
  const [popularNFTs, setPopularNFTs] = useState([]);
  const [stakedAmount, setStakedAmount] = useState('0');
  const [stakingBenefits, setStakingBenefits] = useState({
    mintingDiscount: '0%',
    royaltyRate: '10%'
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [todayStats, setTodayStats] = useState({
    activeTokens: '0',
    nftsMinted: '0',
    transactions: '0',
    visualizations: '0'
  });
  
  // Fetch dashboard data when component mounts
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch new tokens
        await fetchNewTokens();
        
        // Fetch popular NFTs
        await fetchPopularNFTs();
        
        // If wallet is connected, fetch staking info
        if (walletAddress) {
          await fetchStakingInfo(walletAddress);
        }
        
        // Fetch recent activity
        await fetchRecentActivity();
        
        // Fetch newsletter preview
        await fetchNewsletterPreview();
        
        // Fetch today's stats
        await fetchTodayStats();
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [walletAddress]);
  
  const fetchTodayStats = async () => {
    try {
      // In a real implementation, you would fetch live stats for today from your API
      // For demonstration, using sample data that looks like it's from today
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
      console.log(`Fetching stats for today (${formattedDate})`);
      
      // Simulate API call with delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Stats represent:
      // - activeTokens: Number of tokens with activity in the E3D platform today
      // - nftsMinted: New NFTs created from 3D visualizations today
      // - transactions: Number of blockchain transactions processed today
      // - likes: Total number of likes on tokens seen today
      setTodayStats({
        activeTokens: '142',
        nftsMinted: '17',
        transactions: '1.3K',
        likes: '8.6K'
      });
    } catch (error) {
      console.error('Error fetching today\'s stats:', error);
    }
  };
  
  const fetchNewTokens = async () => {
    try {
      // This would normally come from your API
      // Using sample data for demonstration
      const tokens = [
        {
          id: '1',
          name: 'Ethereum',
          symbol: 'ETH',
          logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
          price: '$2,834.25',
          change: '+2.4%',
          positive: true
        },
        {
          id: '2',
          name: 'Solana',
          symbol: 'SOL',
          logo: 'https://cryptologos.cc/logos/solana-sol-logo.png',
          price: '$102.56',
          change: '+5.7%',
          positive: true
        },
        {
          id: '3',
          name: 'Chainlink',
          symbol: 'LINK',
          logo: 'https://cryptologos.cc/logos/chainlink-link-logo.png',
          price: '$13.89',
          change: '-1.2%',
          positive: false
        },
        {
          id: '4',
          name: 'Polygon',
          symbol: 'MATIC',
          logo: 'https://cryptologos.cc/logos/polygon-matic-logo.png',
          price: '$0.98',
          change: '+3.1%',
          positive: true
        }
      ];
      
      setNewTokens(tokens);
    } catch (error) {
      console.error('Error fetching new tokens:', error);
    }
  };
  
  const fetchPopularNFTs = async () => {
    try {
      // Sample NFT data - would normally come from your API
      const nfts = [
        {
          id: '1',
          name: 'Token Universe #42',
          image: 'https://ipfs.io/ipfs/QmR8nGTzZzV6ZBVHZKHMxGNKh1dCULTSDbjUNZsupgKVP5/1.png',
          price: '50 E3D',
          owner: '0x1A8Ab...9BEcbF'
        },
        {
          id: '2',
          name: 'Crypto Network #17',
          image: 'https://ipfs.io/ipfs/QmR8nGTzZzV6ZBVHZKHMxGNKh1dCULTSDbjUNZsupgKVP5/2.png',
          price: '75 E3D',
          owner: '0xD1a688...0AbFAe'
        },
        {
          id: '3',
          name: 'Blockchain Visualization #3',
          image: 'https://ipfs.io/ipfs/QmR8nGTzZzV6ZBVHZKHMxGNKh1dCULTSDbjUNZsupgKVP5/3.png',
          price: '120 E3D',
          owner: '0x6874...712ab5'
        }
      ];
      
      setPopularNFTs(nfts);
    } catch (error) {
      console.error('Error fetching popular NFTs:', error);
    }
  };
  
  const fetchStakingInfo = async (address) => {
    try {
      // In a real implementation, you would call your E3D contracts here
      // For demonstration, using sample data
      setStakedAmount('10.00');
      setStakingBenefits({
        mintingDiscount: '10%',
        royaltyRate: '10%'
      });
    } catch (error) {
      console.error('Error fetching staking info:', error);
    }
  };
  
  const fetchRecentActivity = async () => {
    try {
      // Sample activity data
      const activities = [
        {
          id: '1',
          type: 'mint',
          user: '0x1A8Ab...9BEcbF',
          item: 'Blockchain Visualization #8',
          time: '2 hours ago'
        },
        {
          id: '2',
          type: 'purchase',
          user: '0xD1a688...0AbFAe',
          item: 'Token Universe #42',
          price: '50 E3D',
          time: '5 hours ago'
        },
        {
          id: '3',
          type: 'stake',
          user: '0x6874...712ab5',
          amount: '500 E3D',
          time: '1 day ago'
        }
      ];
      
      setRecentActivity(activities);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };
  
  const fetchNewsletterPreview = async () => {
    try {
      // Sample newsletter preview
      setNewsletterPreview({
        title: 'Weekly Crypto Insights',
        date: 'May 19, 2025',
        excerpt: 'This week we explore the correlation between Ethereum network traffic and token price movements. Our AI analysis indicates potential breakout patterns in several DeFi tokens...'
      });
    } catch (error) {
      console.error('Error fetching newsletter preview:', error);
    }
  };
  
  const navigateTo = (path) => {
    navigate(path);
  };
  
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };
  
  return (
    <div className={classes.dashboardContainer}>
      {/* Navigation Bar */}
      <div className={classes.navBar}>
        <a href="/" className={classes.navLogo}>
          <img src={futLogoIcon200} alt="E3D Logo" className={classes.logoImage} />
          <Typography className={classes.logoText}>E3D</Typography>
        </a>
        <div className={classes.navLinks}>
          <a href="/about" className={classes.navLink}>About</a>
          <a href="/app" className={classes.navLink}>Visualization</a>
          <a href="/marketplace" className={classes.navLink}>Marketplace</a>
          <a href="/swap" className={classes.navLink}>Swap</a>
          <a href="/stake" className={classes.navLink}>Stake</a>
        </div>
        <div className={classes.rightNav}>
          {walletAddress ? (
            <Button 
              size="small" 
              className={classes.walletButton}
              variant="contained"
              onClick={() => navigateTo('/stake')}
            >
              {formatAddress(walletAddress)}
            </Button>
          ) : (
            <Button 
              size="small" 
              className={classes.walletButton}
              variant="contained"
              onClick={() => window.location.href = '/stake'}
            >
              Connect Wallet
            </Button>
          )}
        </div>
      </div>
      
      <div className={classes.mainContent}>
        {/* Today's Stats Widget */}
        <Card className={classes.widgetCard} style={{ marginBottom: 24 }}>
          <div className={classes.widgetTitle}>
            <div className={classes.iconWithLabel}>
              <FaChartBar size={16} />
              <Typography variant="h6">Today's Stats</Typography>
            </div>
          </div>
          <div className={classes.widgetContent}>
            <div className={classes.statsContainer}>
              <div className={classes.statCard}>
                <Typography className={classes.statValue}>{todayStats.activeTokens}</Typography>
                <Typography className={classes.statLabel}>Active Tokens</Typography>
              </div>
              <div className={classes.statCard}>
                <Typography className={classes.statValue}>{todayStats.nftsMinted}</Typography>
                <Typography className={classes.statLabel}>New NFTs</Typography>
              </div>
              <div className={classes.statCard}>
                <Typography className={classes.statValue}>{todayStats.transactions}</Typography>
                <Typography className={classes.statLabel}>Transactions</Typography>
              </div>
              <div className={classes.statCard}>
                <Typography className={classes.statValue}>{todayStats.likes}</Typography>
                <Typography className={classes.statLabel}>Likes</Typography>
              </div>
            </div>
          </div>
        </Card>
        
        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* 3D Visualization Widget */}
          <Grid item xs={12} md={8}>
            <Card className={classes.widgetCard}>
              <div className={classes.widgetTitle}>
                <div className={classes.iconWithLabel}>
                  <FaNetworkWired size={16} />
                  <Typography variant="h6">3D Network Visualization</Typography>
                </div>
                <Button 
                  className={classes.viewAllButton}
                  onClick={() => navigateTo('/token')}
                >
                  Explore Full View
                </Button>
              </div>
              <div className={classes.widgetContent} style={{ padding: 0, overflow: 'hidden' }}>
                {/* Interactive 3D Graph Component */}
                <DashboardGraph height={280} />
                <div style={{ padding: '16px' }}>
                  <Typography variant="body1" style={{ marginTop: 8, marginBottom: 16 }}>
                    Visualize blockchain transactions in an interactive 3D space. Discover connections between tokens and users.
                  </Typography>
                  <div className={classes.graphButtons}>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.actionButton}
                      startIcon={<FaPlus />}
                      onClick={() => navigateTo('/token')}
                    >
                      New Visualization
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.actionButton}
                      startIcon={<FaCubes />}
                      onClick={() => navigateTo('/nft')}
                    >
                      Mint as NFT
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </Grid>
        
        {/* NFT Marketplace Widget */}
        <Grid item xs={12} md={4}>
          <Card className={classes.widgetCard}>
            <div className={classes.widgetTitle}>
              <div className={classes.iconWithLabel}>
                <FaCubes size={16} />
                <Typography variant="h6">Featured NFTs</Typography>
              </div>
              <Button 
                className={classes.viewAllButton}
                onClick={() => navigateTo('/marketplace')}
                endIcon={<FaArrowRight />}
              >
                View Marketplace
              </Button>
            </div>
            <div className={classes.widgetContent}>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#4d79ff' }} />
                </div>
              ) : (
                <>
                  {popularNFTs.map((nft) => (
                    <div key={nft.id} className={classes.marketplaceItem}>
                      <img 
                        src={nft.image} 
                        alt={nft.name} 
                        className={classes.nftImage}
                      />
                      <div style={{ flex: 1 }}>
                        <Typography variant="subtitle2">{nft.name}</Typography>
                        <Typography variant="body2" className={classes.textSecondary}>
                          Owner: {nft.owner}
                        </Typography>
                        <Typography className={classes.nftPrice}>
                          {nft.price}
                        </Typography>
                      </div>
                    </div>
                  ))}
                  <div className={classes.callToAction}>
                    <Button 
                      className={classes.ctaButton}
                      onClick={() => navigateTo('/marketplace')}
                    >
                      Explore All NFTs
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Card>
        </Grid>
        
        {/* Token Discovery Widget */}
        <Grid item xs={12} md={4}>
          <Card className={classes.widgetCard}>
            <div className={classes.widgetTitle}>
              <div className={classes.iconWithLabel}>
                <FaCoins size={16} />
                <Typography variant="h6">New Tokens</Typography>
              </div>
              <Button 
                className={classes.viewAllButton}
                onClick={() => navigateTo('/token')}
                endIcon={<FaArrowRight />}
              >
                View All Tokens
              </Button>
            </div>
            <div className={classes.widgetContent}>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#4d79ff' }} />
                </div>
              ) : (
                <>
                  {newTokens.map((token) => (
                    <div key={token.id} className={classes.tokenItem}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img 
                          src={token.logo} 
                          alt={token.name} 
                          className={classes.tokenLogo}
                        />
                        <div className={classes.tokenInfo}>
                          <Typography className={classes.tokenName}>
                            {token.name} ({token.symbol})
                          </Typography>
                        </div>
                      </div>
                      <div>
                        <Typography className={classes.tokenPrice}>
                          {token.price}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          className={token.positive ? classes.positiveChange : classes.negativeChange}
                        >
                          {token.change}
                        </Typography>
                      </div>
                    </div>
                  ))}
                  <div className={classes.callToAction}>
                    <Button 
                      className={classes.ctaButton}
                      onClick={() => navigateTo('/token')}
                    >
                      Discover Tokens
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Card>
        </Grid>
        
        {/* Staking Widget */}
        <Grid item xs={12} md={4}>
          <Card className={classes.widgetCard}>
            <div className={classes.widgetTitle}>
              <div className={classes.iconWithLabel}>
                <FaMoneyBillWave size={16} />
                <Typography variant="h6">E3D Staking</Typography>
              </div>
              <Button 
                className={classes.viewAllButton}
                onClick={() => navigateTo('/stake')}
                endIcon={<FaArrowRight />}
              >
                Manage Stake
              </Button>
            </div>
            <div className={classes.widgetContent}>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#4d79ff' }} />
                </div>
              ) : (
                <>
                  <div className={classes.stakingStats}>
                    <div className={classes.stakingStatItem}>
                      <Typography className={classes.stakingStatValue}>
                        {stakedAmount} E3D
                      </Typography>
                      <Typography className={classes.stakingStatLabel}>
                        Currently Staked
                      </Typography>
                    </div>
                    <div className={classes.stakingStatItem}>
                      <Typography className={classes.stakingStatValue}>
                        {stakingBenefits.mintingDiscount}
                      </Typography>
                      <Typography className={classes.stakingStatLabel}>
                        Minting Discount
                      </Typography>
                    </div>
                    <div className={classes.stakingStatItem}>
                      <Typography className={classes.stakingStatValue}>
                        {stakingBenefits.royaltyRate}
                      </Typography>
                      <Typography className={classes.stakingStatLabel}>
                        Royalty Rate
                      </Typography>
                    </div>
                  </div>
                  
                  <Typography variant="body2" className={classes.textSecondary} style={{ marginBottom: 16 }}>
                    Stake E3D tokens to receive benefits including NFT minting discounts and increased royalty rates from the marketplace.
                  </Typography>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      className={classes.actionButton}
                      onClick={() => navigateTo('/stake')}
                    >
                      Stake More
                    </Button>
                    <Button 
                      className={classes.actionButton}
                      onClick={() => navigateTo('/stake')}
                      style={{ backgroundColor: '#ff4444' }}
                    >
                      Unstake
                    </Button>
                  </div>
                  
                  <Divider style={{ margin: '16px 0' }} />
                  
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8, color: '#ffffff' }}>
                    Staking Benefits
                  </Typography>
                  <List dense disablePadding>
                    <ListItem style={{ padding: '4px 0' }}>
                      <FaInfoCircle size={14} style={{ color: '#4d79ff', marginRight: 8 }} />
                      <ListItemText primary="100+ E3D: 10% discount on NFT minting" />
                    </ListItem>
                    <ListItem style={{ padding: '4px 0' }}>
                      <FaInfoCircle size={14} style={{ color: '#4d79ff', marginRight: 8 }} />
                      <ListItemText primary="500+ E3D: 20% discount, 7% royalty rate" />
                    </ListItem>
                    <ListItem style={{ padding: '4px 0' }}>
                      <FaInfoCircle size={14} style={{ color: '#4d79ff', marginRight: 8 }} />
                      <ListItemText primary="1000+ E3D: 30% discount, 5% royalty rate" />
                    </ListItem>
                  </List>
                </>
              )}
            </div>
          </Card>
        </Grid>
        
        {/* Swap Widget */}
        <Grid item xs={12} md={4}>
          <Card className={classes.widgetCard}>
            <div className={classes.widgetTitle}>
              <div className={classes.iconWithLabel}>
                <FaExchangeAlt size={16} />
                <Typography variant="h6">Token Swap</Typography>
              </div>
              <Button 
                className={classes.viewAllButton}
                onClick={() => navigateTo('/swap')}
                endIcon={<FaArrowRight />}
              >
                Open Swap
              </Button>
            </div>
            <div className={classes.widgetContent}>
              <Typography variant="body2" className={classes.textSecondary} style={{ marginBottom: 16 }}>
                Easily swap between ETH and E3D tokens with our integrated exchange. Low fees and fast transactions.
              </Typography>
              
              <Paper style={{ backgroundColor: '#272935', padding: 16, borderRadius: 15, marginBottom: 16 }}>
                <Typography variant="body2" style={{ color: '#aaaaaa', marginBottom: 8 }}>
                  Quick Swap
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <img src="https://assets.coingecko.com/coins/images/279/small/ethereum.png" alt="ETH" style={{ width: 24, height: 24, marginRight: 8 }} />
                  <Typography variant="body1">1 ETH</Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                  <FaArrowRight size={16} style={{ color: '#4d79ff' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={futLogoIcon200} alt="E3D" style={{ width: 24, height: 24, marginRight: 8 }} />
                  <Typography variant="body1">789.73 E3D</Typography>
                </div>
              </Paper>
              
              <div className={classes.callToAction}>
                <Button 
                  className={classes.ctaButton}
                  onClick={() => navigateTo('/swap')}
                >
                  Swap Tokens
                </Button>
              </div>
            </div>
          </Card>
        </Grid>
        
        {/* Recent Activity Widget */}
        <Grid item xs={12} md={6}>
          <Card className={classes.widgetCard}>
            <div className={classes.widgetTitle}>
              <div className={classes.iconWithLabel}>
                <FaUsers size={16} />
                <Typography variant="h6">Recent Activity</Typography>
              </div>
              <Button className={classes.viewAllButton} endIcon={<FaArrowRight />}>
                View All
              </Button>
            </div>
            <div className={classes.widgetContent}>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#4d79ff' }} />
                </div>
              ) : (
                <>
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className={classes.activityItem}>
                      <Avatar className={classes.activityAvatar}>
                        {activity.type === 'mint' && <FaCubes color="#4d79ff" />}
                        {activity.type === 'purchase' && <FaExchangeAlt color="#4d79ff" />}
                        {activity.type === 'stake' && <FaMoneyBillWave color="#4d79ff" />}
                      </Avatar>
                      <div className={classes.activityContent}>
                        <Typography variant="body2">
                          {activity.type === 'mint' && (
                            <><strong>{activity.user}</strong> minted <strong>{activity.item}</strong></>
                          )}
                          {activity.type === 'purchase' && (
                            <><strong>{activity.user}</strong> purchased <strong>{activity.item}</strong> for <strong>{activity.price}</strong></>
                          )}
                          {activity.type === 'stake' && (
                            <><strong>{activity.user}</strong> staked <strong>{activity.amount}</strong></>
                          )}
                        </Typography>
                        <Typography variant="caption" className={classes.activityTime}>
                          {activity.time}
                        </Typography>
                      </div>
                      {activity.type !== 'stake' && (
                        <IconButton size="small" style={{ color: '#4d79ff' }}>
                          <FaHeart />
                        </IconButton>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </Card>
        </Grid>
        
        {/* Newsletter Widget */}
        <Grid item xs={12} md={6}>
          <Card className={classes.widgetCard}>
            <div className={classes.widgetTitle}>
              <div className={classes.iconWithLabel}>
                <FaNewspaper size={16} />
                <Typography variant="h6">AI Newsletter</Typography>
              </div>
              <Button 
                className={classes.viewAllButton}
                onClick={() => navigateTo('/news')}
                endIcon={<FaArrowRight />}
              >
                View Archive
              </Button>
            </div>
            <div className={classes.widgetContent}>
              {loading || !newsletterPreview ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#4d79ff' }} />
                </div>
              ) : (
                <>
                  <div className={classes.newsfeedItem}>
                    <Typography className={classes.newsfeedTitle} variant="h6">
                      {newsletterPreview.title}
                    </Typography>
                    <Typography className={classes.newsfeedDate} variant="body2">
                      {newsletterPreview.date}
                    </Typography>
                    <Typography className={classes.newsfeedExcerpt} variant="body2">
                      {newsletterPreview.excerpt}
                    </Typography>
                  </div>
                  
                  <Typography variant="body2" style={{ marginBottom: 16 }}>
                    Our AI-generated newsletter provides insights on market trends, new tokens, and blockchain developments. Stay informed with weekly updates.
                  </Typography>
                  
                  <div className={classes.callToAction}>
                    <Button 
                      className={classes.ctaButton}
                      onClick={() => navigateTo('/news')}
                    >
                      Subscribe to Newsletter
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Card>
        </Grid>
      </Grid>
      </div>
    </div>
  );
};

export default Dashboard;
